import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import {
  HiOutlineBell,
  HiOutlineUserCircle,
  HiOutlineLogout,
  HiOutlineCog,
  HiOutlineNewspaper,
  HiOutlineMap,
  HiOutlineLocationMarker,
  HiOutlineGlobeAlt,
  HiOutlineGlobe,
  HiOutlineBookmark,
  HiOutlineFolderOpen,
  HiOutlineOfficeBuilding,
  HiOutlineShieldCheck,
} from 'react-icons/hi'
import clsx from 'clsx'
import notificationService from '../../services/notificationService'
import { formatDistanceToNow } from 'date-fns'
import { vi } from 'date-fns/locale'

// Navigation items với màu sắc đặc trưng và permission key
const navItems = [
  {
    to: '/news',
    icon: HiOutlineNewspaper,
    label: 'Tin tức',
    featureKey: 'news',
    activeColor: 'bg-blue-100 text-blue-700',
    iconActiveColor: 'text-blue-600',
    hoverColor: 'hover:bg-blue-50 hover:text-blue-600',
  },
  {
    to: '/docs',
    icon: HiOutlineFolderOpen,
    label: 'Tài liệu',
    featureKey: 'docs',
    activeColor: 'bg-amber-100 text-amber-700',
    iconActiveColor: 'text-amber-600',
    hoverColor: 'hover:bg-amber-50 hover:text-amber-600',
  },
  {
    to: '/map',
    icon: HiOutlineMap,
    label: 'Bản đồ giá',
    featureKey: 'map',
    activeColor: 'bg-emerald-100 text-emerald-700',
    iconActiveColor: 'text-emerald-600',
    hoverColor: 'hover:bg-emerald-50 hover:text-emerald-600',
  },
  {
    to: '/maps',
    icon: HiOutlineLocationMarker,
    label: 'Tạo bản đồ',
    featureKey: 'maps',
    activeColor: 'bg-purple-100 text-purple-700',
    iconActiveColor: 'text-purple-600',
    hoverColor: 'hover:bg-purple-50 hover:text-purple-600',
  },
  {
    to: '/projects',
    icon: HiOutlineOfficeBuilding,
    label: 'Dự án',
    featureKey: 'projects',
    activeColor: 'bg-cyan-100 text-cyan-700',
    iconActiveColor: 'text-cyan-600',
    hoverColor: 'hover:bg-cyan-50 hover:text-cyan-600',
  },
]

// Admin menu - dropdown items (only admin)
const adminOnlyMenuItems = [
  { to: '/admin/users', label: 'Người dùng', icon: HiOutlineUserCircle },
  { to: '/admin/teams', label: 'Nhóm', icon: HiOutlineCog },
  { to: '/admin/permissions', label: 'Phân quyền', icon: HiOutlineShieldCheck },
  { to: '/admin/push', label: 'Push Notification', icon: HiOutlineBell },
  { to: '/admin/drive', label: 'Cài đặt Drive', icon: HiOutlineFolderOpen },
]

// Management menu - dropdown items (admin + leader)
const managementMenuItems = [
  { to: '/admin/market-maps', label: 'Bản đồ giá', icon: HiOutlineGlobeAlt },
  { to: '/admin/project-mindmaps', label: 'Quản lý dự án', icon: HiOutlineOfficeBuilding },
  { to: '/admin/iframe-domains', label: 'Iframe Domains', icon: HiOutlineGlobe },
]

// Format time helper
const formatTime = (dateStr) => {
  if (!dateStr) return ''
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: vi })
  } catch {
    return ''
  }
}

function Header() {
  const { user, logout } = useAuthStore()
  const { hasAccess } = usePermissions()
  const location = useLocation()
  const navigate = useNavigate()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const isAdmin = user?.role === 'admin'
  const isLeader = user?.role === 'leader'
  const canManage = isAdmin || isLeader // Admin và Leader đều có quyền quản lý Market Maps, Iframe Domains

  // Filter nav items based on permissions
  const visibleNavItems = useMemo(() => {
    return navItems.filter(item => hasAccess(item.featureKey))
  }, [hasAccess])

  // Notification state
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  // Admin pending users state
  const [pendingUsers, setPendingUsers] = useState({ pending_count: 0, pending_users: [] })

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return

    setIsLoading(true)
    try {
      const result = await notificationService.getNotifications({ limit: 10 })
      if (result.status === 'success' && result.data) {
        setNotifications(result.data.notifications || [])
        setUnreadCount(result.data.unread_count || 0)
      }
    } catch (error) {
      console.error('Fetch notifications error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  // Fetch unread count only (for polling)
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return

    try {
      const count = await notificationService.getUnreadCount()
      setUnreadCount(count)
    } catch (error) {
      console.error('Fetch unread count error:', error)
    }
  }, [user])

  // Fetch pending users count (admin only)
  const fetchPendingUsers = useCallback(async () => {
    if (!user || user.role !== 'admin') return

    try {
      const data = await notificationService.getAdminPendingCount()
      setPendingUsers(data)
    } catch (error) {
      console.error('Fetch pending users error:', error)
    }
  }, [user])

  // Initial fetch
  useEffect(() => {
    fetchNotifications()
    fetchPendingUsers()
  }, [fetchNotifications, fetchPendingUsers])

  // Poll unread count and pending users every 30 seconds
  useEffect(() => {
    if (!user) return

    const interval = setInterval(() => {
      fetchUnreadCount()
      if (user.role === 'admin') {
        fetchPendingUsers()
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [user, fetchUnreadCount, fetchPendingUsers])

  // Fetch when dropdown opens
  useEffect(() => {
    if (showNotifications) {
      fetchNotifications()
      if (isAdmin) {
        fetchPendingUsers()
      }
    }
  }, [showNotifications, fetchNotifications, fetchPendingUsers, isAdmin])

  // Total badge count (notifications + pending users for admin)
  const totalBadgeCount = useMemo(() => {
    const pendingCount = isAdmin ? pendingUsers.pending_count : 0
    return unreadCount + pendingCount
  }, [unreadCount, pendingUsers.pending_count, isAdmin])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowUserMenu(false)
      setShowNotifications(false)
    }
    if (showUserMenu || showNotifications) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showUserMenu, showNotifications])

  // Handle mark all as read
  const handleMarkAllRead = async (e) => {
    e.stopPropagation()
    try {
      await notificationService.markAllAsRead()
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, unread: false })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Mark all read error:', error)
    }
  }

  // Handle pending users notification click (admin only)
  const handlePendingUsersClick = (e) => {
    e.stopPropagation()
    e.preventDefault()
    // Reset pending count locally (will refresh on next fetch)
    setPendingUsers({ pending_count: 0, pending_users: [] })
    setShowNotifications(false)
    navigate('/admin/users?is_approved=false')
  }

  // Handle notification click
  const handleNotificationClick = async (notif) => {
    // Mark as read if unread
    if (!notif.is_read) {
      try {
        await notificationService.markAsRead(notif.id)
        setNotifications(prev =>
          prev.map(n => n.id === notif.id ? { ...n, is_read: true, unread: false } : n)
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      } catch (error) {
        console.error('Mark as read error:', error)
      }
    }

    // Navigate if has url
    if (notif.url) {
      setShowNotifications(false)
      navigate(notif.url)
    }
  }

  const handleLogout = () => {
    logout()
    setShowUserMenu(false)
  }

  return (
    <header className="bg-white border-b border-secondary-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0 sm:py-6">
        <div className="flex items-center justify-between h-14">
          {/* Left: Logo + Navigation */}
          <div className="flex items-center gap-2">
            {/* Logo */}
            <Link to="/news" className="flex items-center group mr-2">
              <img
                src="/logo_dat_xanh_mien_trung.png"
                alt="Đất Xanh Miền Trung"
                className="h-8 sm:h-10 w-auto object-contain"
              />
            </Link>

            {/* Divider */}
            <div className="hidden md:block w-px h-6 bg-secondary-200 mx-1" />

            {/* Main Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                      isActive
                        ? item.activeColor
                        : `text-secondary-600 ${item.hoverColor}`
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className={clsx('w-4 h-4', isActive ? item.iconActiveColor : '')} />
                      <span>{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}

              {/* Management Dropdown - Admin và Leader đều thấy */}
              {canManage && (
                <>
                  <div className="w-px h-5 bg-secondary-200 mx-1" />
                  <div className="relative group">
                    <button
                      className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                        location.pathname.startsWith('/admin')
                          ? 'bg-red-100 text-red-700'
                          : 'text-secondary-600 hover:bg-red-50 hover:text-red-600'
                      )}
                    >
                      <HiOutlineCog className="w-4 h-4" />
                      <span>Quản lý</span>
                    </button>
                    {/* Dropdown */}
                    <div className="absolute left-0 top-full pt-1 hidden group-hover:block z-50">
                      <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-1 min-w-[160px]">
                        {/* Management items - Admin + Leader */}
                        {managementMenuItems.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            className={clsx(
                              'flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                              location.pathname === item.to
                                ? 'bg-red-50 text-red-700'
                                : 'text-gray-700 hover:bg-gray-50'
                            )}
                          >
                            <item.icon className="w-4 h-4" />
                            {item.label}
                          </Link>
                        ))}
                        {/* Admin only items */}
                        {isAdmin && adminOnlyMenuItems.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            className={clsx(
                              'flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                              location.pathname === item.to
                                ? 'bg-red-50 text-red-700'
                                : 'text-gray-700 hover:bg-gray-50'
                            )}
                          >
                            <item.icon className="w-4 h-4" />
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </nav>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowNotifications(!showNotifications)
                  setShowUserMenu(false)
                }}
                className={clsx(
                  'relative p-2 rounded-lg transition-colors',
                  showNotifications ? 'bg-secondary-100' : 'hover:bg-secondary-50'
                )}
              >
                <HiOutlineBell className="w-5 h-5 text-secondary-600" />
                {totalBadgeCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-2xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {totalBadgeCount > 9 ? '9+' : totalBadgeCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div
                  className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg border border-secondary-100 z-20 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-3 border-b border-secondary-100 flex items-center justify-between bg-secondary-50">
                    <h3 className="font-semibold text-secondary-900">
                      Thông báo
                      {unreadCount > 0 && (
                        <span className="ml-2 text-xs font-normal text-secondary-500">
                          ({unreadCount} chưa đọc)
                        </span>
                      )}
                    </h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Đánh dấu đã đọc
                      </button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {/* Admin: Pending users notification */}
                    {isAdmin && pendingUsers.pending_count > 0 && (
                      <button
                        onClick={handlePendingUsersClick}
                        className="w-full px-4 py-3 text-left bg-amber-50 hover:bg-amber-100 transition-colors border-b border-amber-200 cursor-pointer"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                            <HiOutlineUserCircle className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-amber-800">
                              {pendingUsers.pending_count} tài khoản chờ phê duyệt
                            </p>
                            <p className="text-xs text-amber-600 mt-0.5">
                              {pendingUsers.pending_users.slice(0, 2).map(u => u.full_name).join(', ')}
                              {pendingUsers.pending_count > 2 && ` và ${pendingUsers.pending_count - 2} người khác`}
                            </p>
                            <p className="text-xs text-amber-500 mt-1">Nhấn để xem danh sách</p>
                          </div>
                        </div>
                      </button>
                    )}

                    {isLoading && notifications.length === 0 ? (
                      <div className="p-4 text-center text-secondary-500 text-sm">
                        Đang tải...
                      </div>
                    ) : notifications.length === 0 && (!isAdmin || pendingUsers.pending_count === 0) ? (
                      <div className="p-8 text-center">
                        <HiOutlineBell className="w-10 h-10 text-secondary-300 mx-auto mb-2" />
                        <p className="text-secondary-500 text-sm">Chưa có thông báo</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <button
                          key={notif.id}
                          onClick={() => handleNotificationClick(notif)}
                          className={clsx(
                            'w-full px-4 py-3 text-left hover:bg-secondary-50 transition-colors border-b border-secondary-50 last:border-0',
                            !notif.is_read && 'bg-blue-50/50'
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className={clsx(
                              'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
                              !notif.is_read ? 'bg-blue-500' : 'bg-secondary-300'
                            )} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-secondary-800 line-clamp-2">{notif.title}</p>
                              {notif.body && (
                                <p className="text-xs text-secondary-500 line-clamp-1 mt-0.5">{notif.body}</p>
                              )}
                              <p className="text-xs text-secondary-400 mt-1">{formatTime(notif.created_at)}</p>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Avatar & Menu */}
            <div className="relative ml-1">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowUserMenu(!showUserMenu)
                  setShowNotifications(false)
                }}
                className={clsx(
                  'flex items-center gap-2 p-1.5 rounded-lg transition-colors',
                  showUserMenu ? 'bg-secondary-100' : 'hover:bg-secondary-50'
                )}
              >
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div className="hidden lg:block text-left max-w-[100px]">
                  <p className="text-sm font-medium text-secondary-900 truncate leading-tight">
                    {user?.full_name || 'User'}
                  </p>
                  <p className="text-2xs text-secondary-400 capitalize leading-tight">
                    {user?.role === 'admin' ? 'Admin' : 'Sales'}
                  </p>
                </div>
              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <div
                  className="absolute right-0 mt-2 w-52 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg border border-secondary-100 z-20 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* User Info Header */}
                  <div className="px-4 py-3 bg-gradient-to-r from-primary-50 to-blue-50 border-b border-secondary-100">
                    <p className="font-semibold text-secondary-900 truncate">{user?.full_name}</p>
                    <p className="text-xs text-secondary-500 truncate mt-0.5">{user?.email}</p>
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    <Link
                      to="/saved"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <HiOutlineBookmark className="w-5 h-5 text-primary-500" />
                      <span>Tin đã lưu</span>
                    </Link>

                    <Link
                      to="/profile"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <HiOutlineUserCircle className="w-5 h-5 text-secondary-400" />
                      <span>Thông tin cá nhân</span>
                    </Link>

                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <HiOutlineCog className="w-5 h-5 text-secondary-400" />
                      <span>Cài đặt</span>
                    </Link>
                  </div>

                  <div className="border-t border-secondary-100 py-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors w-full"
                    >
                      <HiOutlineLogout className="w-5 h-5" />
                      <span>Đăng xuất</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
